import React, { useRef } from 'react';
import { Space, Modal, Row, Col, Typography, Button, Upload, Divider, Card, message } from 'antd';
import { 
  UploadOutlined, 
  DownloadOutlined, 
  FileAddOutlined,
  CodeOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;

const WorkflowActionsModal = ({ 
  visible, 
  onCancel, 
  onExport, 
  onImport,
  workflowId,
  onOpenEmbedModal
}) => {
  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.json',
    showUploadList: false,
    beforeUpload: (file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = JSON.parse(e.target.result);
          if (!content.nodes || !content.edges) {
            throw new Error('Invalid workflow file format');
          }
          onImport(content);
        } catch (error) {
          message.error('Error importing workflow: ' + error.message);
        }
      };
      reader.readAsText(file);
      return false;
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      width={900}
      title="Workflow Import/Export"
      footer={null}
    >
      <Row gutter={24}>
        <Col span={12}>
          <Card style={{ height: '100%' }}>
            <Title level={4}>
              <DownloadOutlined /> Export Workflow
            </Title>
            <Paragraph>
              Export your workflow to share it with others or save it for later use. 
              The exported file will include:
            </Paragraph>
            <ul>
              <li><Text strong>Workflow Structure</Text>: All nodes and their connections</li>
              <li><Text strong>Node Settings</Text>: Configuration of each node</li>
              <li><Text strong>Layout Information</Text>: Position of nodes in the workspace</li>
            </ul>
            <Paragraph>
              You can use exported workflows to:
            </Paragraph>
            <ul>
              <li>Share workflows with team members</li>
              <li>Back up your configurations</li>
              <li>Move workflows between different WordPress installations</li>
              <li>Create templates for similar workflows</li>
            </ul>
            <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={onExport}
                size="large"
                block
              >
                Export Workflow
              </Button>
              
              {workflowId && (
                <Button 
                  type="default"
                  icon={<CodeOutlined />} 
                  onClick={onOpenEmbedModal}
                  size="large"
                  block
                >
                  Get Embed Code
                </Button>
              )}
            </Space>
          </Card>
        </Col>

        <Col span={12}>
          <Card style={{ height: '100%' }}>
            <Title level={4}>
              <UploadOutlined /> Import Workflow
            </Title>
            <Paragraph>
              Import a workflow from a .json file. You can either:
            </Paragraph>
            <Dragger {...uploadProps} style={{ padding: '20px', marginBottom: '20px' }}>
              <p className="ant-upload-drag-icon">
                <FileAddOutlined />
              </p>
              <p className="ant-upload-text">Click or drag workflow file to this area</p>
              <p className="ant-upload-hint">
                Support for .json workflow files exported from WP AI Workflow Automation
              </p>
            </Dragger>
            <Paragraph>
              <Text type="secondary">
                Note: Importing a workflow will replace your current workspace. Make sure to save any unsaved changes before importing.
              </Text>
            </Paragraph>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default WorkflowActionsModal;